import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Mail, Phone, Calendar, FileText, DollarSign, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteRequests = () => {
  const { getAuthHeader } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQuotes = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quote`, {
        headers: getAuthHeader()
      });
      setQuotes(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching quote requests:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Quote Requests</h1>
        <p className="text-gray-600 mt-2">
          View and manage quote requests from potential clients
        </p>
      </div>

      {quotes.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <DollarSign className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No quote requests yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {quotes.map((quote) => (
            <Card key={quote.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {/* Client Info */}
                  <div>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {quote.name}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {quote.company || 'Individual'}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {quote.status || 'New'}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`mailto:${quote.email}`} className="hover:text-primary truncate">
                          {quote.email}
                        </a>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`tel:${quote.phone}`} className="hover:text-primary">
                          {quote.phone}
                        </a>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {formatDate(quote.created_at)}
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Project Details</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-500">Project Type:</span>
                        <p className="font-medium text-gray-900">{quote.project_type}</p>
                      </div>
                      {quote.budget_range && (
                        <div>
                          <span className="text-gray-500">Budget Range:</span>
                          <p className="font-medium text-gray-900">{quote.budget_range}</p>
                        </div>
                      )}
                      {quote.timeline && (
                        <div>
                          <span className="text-gray-500">Timeline:</span>
                          <p className="font-medium text-gray-900">{quote.timeline}</p>
                        </div>
                      )}
                      {quote.location && (
                        <div>
                          <span className="text-gray-500">Location:</span>
                          <p className="font-medium text-gray-900">{quote.location}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Description & Actions */}
                  <div>
                    {quote.description && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <div className="flex items-start mb-2">
                          <FileText className="w-4 h-4 mr-2 text-gray-400 mt-1" />
                          <p className="text-sm font-medium text-gray-700">Description:</p>
                        </div>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-4">
                          {quote.description}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => window.location.href = `mailto:${quote.email}?subject=Re: Quote Request - ${quote.project_type}`}
                        className="w-full bg-primary hover:bg-primary/90"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Quote
                      </Button>
                      <Button
                        onClick={() => window.location.href = `tel:${quote.phone}`}
                        variant="outline"
                        className="w-full"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        Call Client
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuoteRequests;